import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { EVENT_MAP, eventNumbers, eventMinute, visibleTypes, formatTime, targetLabel, teamName, scoreText, PERIOD_SHORT, maxOpportunities } from "@/lib/events";
import { playerSummary } from "@/lib/players";
import { periodSummary, hasTeamless } from "@/lib/periods";
import { DISC_KEYS, disciplineText } from "@/lib/discipline";
import { subsSummary } from "@/components/SubsByTeam";
import { shootoutRows } from "@/components/ShootoutSummary";
import { expulsionRows } from "@/components/ExpulsionsSummary";
import { keyMomentRows } from "@/components/KeyMoments";

const fileBase = (m) => `jogo_${m.home.name}_vs_${m.away.name}`.replace(/\s+/g, "_");

const rows = (m) => {
  const nums = eventNumbers(m);
  return [...m.events].sort((a, b) => nums[a.id] - nums[b.id]).map((e) => [
    nums[e.id],
    PERIOD_SHORT[e.period || 1],
    eventMinute(e, m.half2_reset),
    formatTime(e.seconds),
    EVENT_MAP[e.type].label,
    teamName(m, e.team),
    e.target_type === "official" ? "Oficial" : e.target_type === "player" ? "Jogador" : "—",
    targetLabel(e),
    e.zone || "—",
    e.note || "",
  ]);
};

const HEAD = ["Nº", "Parte", "Minuto", "Cronómetro", "Evento", "Equipa", "Tipo", "Identificação", "Zona", "Observação"];

export const exportCSV = (m) => {
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [HEAD, ...rows(m)].map((r) => r.map(esc).join(";"));
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${fileBase(m)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const countBy = (m, type, team) => m.events.filter((e) => e.type === type && e.team === team).length;

export const exportPDF = async (m) => {
  const doc = new jsPDF();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(`${m.home.name} ${scoreText(m, " - ")} ${m.away.name}`, 14, 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text([m.competition, m.date, m.venue].filter(Boolean).join("  ·  ") || "Relatório de jogo", 14, 25);

  autoTable(doc, {
    startY: 32,
    head: [["Lances relevantes · Minuto", "Lance", "Equipa", "Identificação", "Zona", "Observação"]],
    body: keyMomentRows(m).length ? keyMomentRows(m) : [["Sem lances relevantes", "", "", "", "", ""]],
    theme: "grid",
    headStyles: { fillColor: [139, 30, 24] },
    styles: { fontSize: 9 },
    columnStyles: { 5: { cellWidth: 55 } },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["Resumo", m.home.name, m.away.name, "Sem equipa"]],
    body: visibleTypes(m).length ? visibleTypes(m).map((t) => [t.label, countBy(m, t.key, "home"), countBy(m, t.key, "away"), countBy(m, t.key, null)]) : [["Sem eventos registados", "", "", ""]],
    theme: "grid",
    headStyles: { fillColor: [10, 10, 10] },
    styles: { fontSize: 9 },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["Disciplina e penáltis", m.home.name, m.away.name]],
    body: [
      ...DISC_KEYS.map((k) => [EVENT_MAP[k].label, disciplineText(m, "home", k), disciplineText(m, "away", k)]),
      ["Oportunidades de substituição", ...["home", "away"].map((s) => {
        const x = subsSummary(m, s);
        return `${x.used}/${maxOpportunities(m)} usadas · ${x.total} subst.${x.groups.length ? "\n" + x.groups.map((g) => `${g.opp ? `Op. ${g.opp}` : "S/ op."} (${g.minute}): ${g.pairs.join(", ")}`).join("\n") : ""}`;
      })],
    ],
    theme: "grid",
    headStyles: { fillColor: [10, 10, 10] },
    styles: { fontSize: 9 },
    columnStyles: { 0: { cellWidth: 40 } },
  });

  const exp = expulsionRows(m);
  if (exp.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Expulsões · Minuto", "Equipa", "Expulso", "Motivo", "Ficou com"]],
      body: exp,
      theme: "grid",
      headStyles: { fillColor: [180, 20, 20] },
      styles: { fontSize: 9 },
    });
  }

  if (m.shootout?.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Penáltis (desempate)", "Equipa", "Jogador", "Resultado", "Marcador"]],
      body: shootoutRows(m),
      theme: "grid",
      headStyles: { fillColor: [10, 10, 10] },
      styles: { fontSize: 9 },
    });
  }

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["Período", m.home.name, m.away.name, ...(hasTeamless(m) ? ["Sem equipa"] : [])]],
    body: periodSummary(m).map((b) => [b.label, b.home.text, b.away.text, ...(hasTeamless(m) ? [b.none.text] : [])]),
    theme: "grid",
    headStyles: { fillColor: [10, 10, 10] },
    styles: { fontSize: 9 },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [HEAD],
    body: rows(m),
    theme: "striped",
    headStyles: { fillColor: [10, 10, 10] },
    styles: { fontSize: 9 },
  });

  const ps = playerSummary(m);
  if (ps.rows.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Resumo por jogador", ...ps.cols.map((t) => t.short), "Entrou", "Saiu"]],
      body: ps.rows.map((r) => [`${m[r.team].name} · Nº ${r.number}`, ...ps.cols.map((t) => r.counts[t.key] || ""), r.in || "", r.out || ""]),
      theme: "grid",
      headStyles: { fillColor: [10, 10, 10] },
      styles: { fontSize: 8, halign: "center" },
      columnStyles: { 0: { halign: "left", cellWidth: 48 } },
    });
  }

  const img = await pitchImage();
  if (img) {
    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Mapa de eventos", 14, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`${img.vertical ? `${m.home.name} em cima · ${m.away.name} em baixo` : `${m.home.name} à esquerda · ${m.away.name} à direita`} · números = Nº do evento na cronologia (filtros atuais do mapa)`, 14, 24);
    let w = 182, h = w * img.ratio;
    if (h > 250) { h = 250; w = h / img.ratio; }
    doc.addImage(img.data, "PNG", 14 + (182 - w) / 2, 30, w, h);
  }
  doc.save(`${fileBase(m)}.pdf`);
};

const pitchImage = () => new Promise((resolve) => {
  const svg = document.querySelector('[data-testid="report-pitch"]');
  if (!svg) return resolve(null);
  const vb = svg.viewBox.baseVal;
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = vb.width * 16;
    c.height = vb.height * 16;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve({ data: c.toDataURL("image/png"), ratio: vb.height / vb.width, vertical: vb.height > vb.width });
  };
  img.onerror = () => resolve(null);
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(new XMLSerializer().serializeToString(svg));
});
